import { trace, context, propagation, metrics, SpanStatusCode } from '@opentelemetry/api';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { Resource } from '@opentelemetry/resources';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { BatchSpanProcessor, ConsoleSpanExporter } from '@opentelemetry/sdk-trace-web';

interface TelemetryConfig {
  serviceName?: string;
  serviceVersion?: string;
  environment?: string;
  otlpEndpoint?: string;
  consoleExport?: boolean;
  enableUserInteraction?: boolean;
  enableDocumentLoad?: boolean;
  otlpHeaders?: Record<string, string>;
  otlpFormat?: 'json' | 'protobuf';
}

class VoxiTelemetryBrowser {
  private tracer?: WebTracerProvider;
  private meter?: MeterProvider;
  private config: TelemetryConfig;
  
  constructor(config: TelemetryConfig = {}) {
    // Parse OTLP headers from environment variable if provided
    let otlpHeaders = {};
    try {
      otlpHeaders = import.meta.env.VITE_OTEL_HEADERS 
        ? JSON.parse(import.meta.env.VITE_OTEL_HEADERS) 
        : {};
    } catch (error) {
    }

    this.config = {
      serviceName: import.meta.env.VITE_OTEL_SERVICE_NAME || 'voxi-frontend',
      serviceVersion: import.meta.env.VITE_OTEL_SERVICE_VERSION || '1.0.0',
      environment: import.meta.env.VITE_OTEL_ENVIRONMENT || import.meta.env.MODE || 'development',
      otlpEndpoint: import.meta.env.VITE_OTEL_ENDPOINT,
      // EMERGENCY: Always enable console export if no OTLP endpoint is configured
      consoleExport: import.meta.env.VITE_OTEL_CONSOLE_EXPORT === 'true' || !import.meta.env.VITE_OTEL_ENDPOINT,
      enableUserInteraction: true,
      enableDocumentLoad: true,
      otlpHeaders,
      otlpFormat: (import.meta.env.VITE_OTEL_FORMAT as 'json' | 'protobuf') || 'json',
      ...config
    };
    
  }
  
  init(): void {
    const resource = new Resource({
      [SemanticResourceAttributes.SERVICE_NAME]: this.config.serviceName,
      [SemanticResourceAttributes.SERVICE_VERSION]: this.config.serviceVersion,
      [SemanticResourceAttributes.DEPLOYMENT_ENVIRONMENT]: this.config.environment,
      'browser.user_agent': navigator.userAgent,
      'browser.language': navigator.language,
    });
    
    // Initialize tracing
    this.initTracing(resource);
    
    // Initialize metrics
    this.initMetrics(resource);
    
    // Register manual instrumentations (browser-safe)
    this.registerManualInstrumentations();
    
    // Test span creation immediately after init
    setTimeout(() => this.createTestSpan(), 100);
  }
  
  private initTracing(resource: Resource): void {
    this.tracer = new WebTracerProvider({
      resource,
    });
    
    // Console exporter for development
    if (this.config.consoleExport) {
      const consoleExporter = new ConsoleSpanExporter();
      this.tracer.addSpanProcessor(new BatchSpanProcessor(consoleExporter));
    }
    
    // OTLP exporter for production
    if (this.config.otlpEndpoint) {
      // Handle different endpoint formats
      const baseUrl = this.config.otlpEndpoint.endsWith('/') 
        ? this.config.otlpEndpoint.slice(0, -1) 
        : this.config.otlpEndpoint;
      
      const traceUrl = baseUrl.includes('/v1/traces') 
        ? baseUrl 
        : `${baseUrl}/v1/traces`;
        
      
      try {
        // Debug: Log the exact configuration being used
        const contentType = this.config.otlpFormat === 'protobuf' 
          ? 'application/x-protobuf'
          : 'application/json';
          
        const exporterConfig = {
          url: traceUrl,
          headers: {
            'Content-Type': contentType,
            'Accept': 'application/json, application/x-protobuf',
            ...this.config.otlpHeaders || {},
          },
        };
        
        
        const otlpExporter = new OTLPTraceExporter(exporterConfig);
        
        // Add error handling to the exporter
        const batchProcessor = new BatchSpanProcessor(otlpExporter, {
          // Add debugging for export failures
          exportTimeoutMillis: 10000,
          maxExportBatchSize: 512,
        });
        
        // Wrap exporter to catch and log errors
        // Add network request debugging
        this.addNetworkDebugging(traceUrl);
        
        const originalExport = otlpExporter.export.bind(otlpExporter);
        otlpExporter.export = (spans, resultCallback) => {
          
          return originalExport(spans, (result) => {
            if (result.code !== 0) {
              
              // Log first span for debugging with full details
              if (spans.length > 0) {
                const firstSpan = spans[0];
              }
            } else {
            }
            resultCallback(result);
          });
        };
        
        this.tracer.addSpanProcessor(batchProcessor);
        
      } catch (error) {
      }
    }
    
    this.tracer.register();
  }
  
  private initMetrics(resource: Resource): void {
    // OTLP metric exporter
    if (this.config.otlpEndpoint) {
      const baseUrl = this.config.otlpEndpoint.endsWith('/') 
        ? this.config.otlpEndpoint.slice(0, -1) 
        : this.config.otlpEndpoint;
      
      const metricsUrl = baseUrl.includes('/v1/metrics') 
        ? baseUrl 
        : `${baseUrl}/v1/metrics`;
        
      
      const metricExporter = new OTLPMetricExporter({
        url: metricsUrl,
        headers: this.config.otlpHeaders || {},
      });
      
      this.meter = new MeterProvider({
        resource,
        readers: [
          new PeriodicExportingMetricReader({
            exporter: metricExporter,
            exportIntervalMillis: 30000, // 30 seconds
          }),
        ],
      });
      
      metrics.setGlobalMeterProvider(this.meter);
    }
  }
  
  private registerManualInstrumentations(): void {
    // Manual fetch instrumentation (browser-safe)
    if (typeof window !== 'undefined' && window.fetch) {
      this.instrumentFetch();
    }

    // Manual XMLHttpRequest instrumentation (browser-safe)
    if (typeof window !== 'undefined' && window.XMLHttpRequest) {
      this.instrumentXHR();
    }

    // Document load instrumentation (browser-safe)
    if (this.config.enableDocumentLoad) {
      this.instrumentDocumentLoad();
    }

    // User interaction instrumentation (browser-safe)
    if (this.config.enableUserInteraction) {
      this.instrumentUserInteractions();
    }
  }

  private instrumentFetch(): void {
    const originalFetch = window.fetch;
    const serviceName = this.config.serviceName!;
    const self = this;
    
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const method = init?.method || 'GET';
      
      const attributes = self.validateAttributes({
        'http.method': method,
        'http.url': url,
        'http.target': new URL(url, window.location.href).pathname,
      });
      
      const span = trace.getTracer(serviceName).startSpan(`HTTP ${method}`, {
        attributes,
      });

      return context.with(trace.setSpan(context.active(), span), async () => {
        try {
          const startTime = performance.now();
          const response = await originalFetch(input, init);
          const endTime = performance.now();
          
          span.setAttributes({
            'http.status_code': response.status,
            'http.response.content_length': response.headers.get('content-length') || 0,
            'http.duration': endTime - startTime,
          });
          
          if (response.ok) {
            span.setStatus({ code: SpanStatusCode.OK });
          } else {
            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: `HTTP ${response.status}: ${response.statusText}`,
            });
          }
          
          return response;
        } catch (error) {
          span.recordException(error as Error);
          span.setStatus({
            code: SpanStatusCode.ERROR,
            message: error instanceof Error ? error.message : 'Unknown error',
          });
          throw error;
        } finally {
          span.end();
        }
      });
    };
  }

  private instrumentXHR(): void {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    const serviceName = this.config.serviceName!;
    const self = this;

    XMLHttpRequest.prototype.open = function(method: string, url: string | URL, ...args: any[]) {
      const urlString = typeof url === 'string' ? url : url.href;
      (this as any)._telemetryData = {
        method: method.toUpperCase(),
        url: urlString,
        startTime: 0,
        span: null,
      };
      return originalOpen.call(this, method, url, ...args);
    };

    XMLHttpRequest.prototype.send = function(body?: any) {
      const telemetryData = (this as any)._telemetryData;
      if (telemetryData) {
        telemetryData.startTime = performance.now();
        const attributes = self.validateAttributes({
          'http.method': telemetryData.method,
          'http.url': telemetryData.url,
          'http.target': new URL(telemetryData.url, window.location.href).pathname,
        });
        
        telemetryData.span = trace.getTracer(serviceName).startSpan(`HTTP ${telemetryData.method}`, {
          attributes,
        });

        this.addEventListener('loadend', () => {
          if (telemetryData.span) {
            const endTime = performance.now();
            telemetryData.span.setAttributes({
              'http.status_code': this.status,
              'http.duration': endTime - telemetryData.startTime,
            });

            if (this.status >= 200 && this.status < 400) {
              telemetryData.span.setStatus({ code: SpanStatusCode.OK });
            } else {
              telemetryData.span.setStatus({
                code: SpanStatusCode.ERROR,
                message: `HTTP ${this.status}: ${this.statusText}`,
              });
            }
            telemetryData.span.end();
          }
        });
      }
      return originalSend.call(this, body);
    };
  }

  private instrumentDocumentLoad(): void {
    if (document.readyState === 'complete') {
      this.recordDocumentLoadMetrics();
    } else {
      window.addEventListener('load', () => this.recordDocumentLoadMetrics());
    }
  }

  private recordDocumentLoadMetrics(): void {
    if (typeof window !== 'undefined' && window.performance) {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        // Helper to safely calculate timing differences and avoid null values
        const safeTiming = (end: number, start: number): number => {
          const diff = end - start;
          return (!isFinite(diff) || diff < 0) ? 0 : Math.round(diff);
        };
        
        // Only include valid timing metrics to prevent null doubleValue errors
        const attributes: Record<string, number> = {};
        
        const dnsLookup = safeTiming(navigation.domainLookupEnd, navigation.domainLookupStart);
        const tcpConnect = safeTiming(navigation.connectEnd, navigation.connectStart);
        const request = safeTiming(navigation.responseStart, navigation.requestStart);
        const response = safeTiming(navigation.responseEnd, navigation.responseStart);
        const domProcessing = safeTiming(navigation.domComplete, navigation.domLoading);
        const total = safeTiming(navigation.loadEventEnd, navigation.fetchStart);
        
        // Only add metrics that have valid values
        if (dnsLookup > 0) attributes['document.load.dns_lookup'] = dnsLookup;
        if (tcpConnect > 0) attributes['document.load.tcp_connect'] = tcpConnect;
        if (request > 0) attributes['document.load.request'] = request;
        if (response > 0) attributes['document.load.response'] = response;
        if (domProcessing > 0) attributes['document.load.dom_processing'] = domProcessing;
        if (total > 0) attributes['document.load.total'] = total;
        
        
        const span = trace.getTracer(this.config.serviceName!).startSpan('document.load', {
          attributes,
        });
        span.setStatus({ code: SpanStatusCode.OK });
        span.end();
      }
    }
  }

  private instrumentUserInteractions(): void {
    const serviceName = this.config.serviceName!;
    const eventTypes = ['click', 'submit', 'change'];

    eventTypes.forEach(eventType => {
      document.addEventListener(eventType, (event) => {
        const target = event.target as Element;
        if (target && !target.classList.contains('no-telemetry')) {
          const attributes = this.validateAttributes({
            'user.interaction.type': eventType,
            'user.interaction.target': target.tagName.toLowerCase(),
            'user.interaction.target_id': target.id || '',
            'user.interaction.target_class': target.className || '',
          });
          
          const span = trace.getTracer(serviceName).startSpan(`user.${eventType}`, {
            attributes,
          });
          span.setStatus({ code: SpanStatusCode.OK });
          span.end();
        }
      }, { passive: true, capture: true });
    });
  }
  
  // Helper methods for manual instrumentation
  
  startSpan(name: string, options?: any) {
    const tracer = trace.getTracer(this.config.serviceName!);
    
    // Clean attributes to prevent null doubleValue errors
    if (options?.attributes) {
      options.attributes = this.validateAttributes(options.attributes);
    }
    
    return tracer.startSpan(name, options);
  }
  
  async withSpan<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const span = this.startSpan(name);
    
    try {
      const result = await fn();
      span.setStatus({ code: SpanStatusCode.OK });
      return result;
    } catch (error) {
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      span.recordException(error as Error);
      throw error;
    } finally {
      span.end();
    }
  }
  
  // Custom metrics for Voxi
  
  createCustomMetrics() {
    const meter = metrics.getMeter(this.config.serviceName!);
    
    // Video call violation metrics
    const violationCounter = meter.createCounter('voxi.video.violations', {
      description: 'Count of video call violations',
    });
    
    // Call duration histogram
    const callDurationHistogram = meter.createHistogram('voxi.call.duration', {
      description: 'Duration of calls in seconds',
      unit: 's',
    });
    
    // API latency histogram
    const apiLatencyHistogram = meter.createHistogram('voxi.api.latency', {
      description: 'API request latency in milliseconds',
      unit: 'ms',
    });
    
    // Page load time histogram
    const pageLoadHistogram = meter.createHistogram('voxi.page.load_time', {
      description: 'Page load time in milliseconds',
      unit: 'ms',
    });
    
    return {
      violationCounter,
      callDurationHistogram,
      apiLatencyHistogram,
      pageLoadHistogram,
    };
  }
  
  // Test span creation to verify telemetry is working
  private createTestSpan(): void {
    try {
      const testSpan = this.startSpan('telemetry.init_test', {
        attributes: {
          'test.purpose': 'verify_telemetry_working',
          'test.timestamp': Date.now(),
          'test.environment': this.config.environment!,
          'test.service': this.config.serviceName!,
        }
      });
      
      testSpan.addEvent('telemetry_test_event', {
        message: 'Telemetry system is working',
        timestamp: new Date().toISOString(),
      });
      
      
      testSpan.setStatus({ code: SpanStatusCode.OK });
      testSpan.end();
      
    } catch (error) {
    }
  }
  
  // Helper to trace React components
  traceComponent(componentName: string) {
    return <P extends object>(Component: React.ComponentType<P>): React.ComponentType<P> => {
      const TracedComponent = (props: P) => {
        const span = this.startSpan(`component.${componentName}.render`);
        
        try {
          const result = Component(props);
          span.setStatus({ code: SpanStatusCode.OK });
          return result;
        } catch (error) {
          span.setStatus({
            code: SpanStatusCode.ERROR,
            message: error instanceof Error ? error.message : 'Unknown error',
          });
          span.recordException(error as Error);
          throw error;
        } finally {
          span.end();
        }
      };
      
      TracedComponent.displayName = `Traced(${componentName})`;
      return TracedComponent;
    };
  }
  
  // Helper to trace async functions
  traceAsync<T extends (...args: any[]) => Promise<any>>(
    name: string,
    fn: T
  ): T {
    return (async (...args: Parameters<T>) => {
      return this.withSpan(name, () => fn(...args));
    }) as T;
  }
  
  // Validate and clean attributes to prevent null doubleValue errors
  private validateAttributes(attributes: Record<string, any>): Record<string, any> {
    const cleaned: Record<string, any> = {};
    
    for (const [key, value] of Object.entries(attributes)) {
      // Skip null, undefined, or NaN values
      if (value === null || value === undefined) {
        continue;
      }
      
      // For numbers, ensure they're finite
      if (typeof value === 'number') {
        if (!isFinite(value)) {
          continue;
        }
      }
      
      cleaned[key] = value;
    }
    
    return cleaned;
  }

  // Add network request debugging for OTLP exports
  private addNetworkDebugging(targetUrl: string): void {
    // Only add once to avoid multiple interceptors
    if ((window as any).__otlpNetworkDebugAdded) return;
    (window as any).__otlpNetworkDebugAdded = true;
    
    const originalFetch = window.fetch;
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const [url, init] = args;
      const urlStr = url?.toString() || '';
      
      // Only intercept our OTLP requests
      if (urlStr.includes('/v1/traces')) {
        
        try {
          const response = await originalFetch(...args);
          
          if (!response.ok) {
            // Get response body for 400 errors
            const responseClone = response.clone();
            const errorBody = await responseClone.text().catch(() => 'Failed to read response');
            
          } else {
          }
          
          return response;
        } catch (error) {
          throw error;
        }
      }
      
      return originalFetch(...args);
    };
  }
}

// Export singleton instance
export const telemetry = new VoxiTelemetryBrowser();

// Export types and utilities
export { trace, context, propagation, SpanStatusCode };
export type { TelemetryConfig };